//
// Lab boot. Waits (capped rAF poll, same pattern the old lab.ts used) for the
// four window globals the sections consume — HaikuFX and HaikuFeed are set by
// starfield.js/library.js module scripts, so they may land a tick after this
// module runs — then builds the sheet and fills the section panels.
//
// Section builders are imported directly (Tasks 3-5 add one import + call
// each); this file stays a thin composition root.
//
import { buildSheet, getState, getTab, setState, setTab } from "./sheet.js";
import { buildWeather } from "./sections/weather.js";
import { buildFeed } from "./sections/feed.js";
import { buildChips, buildTheme, copyAllJSON } from "./sections/theme.js";
import { buildAlchemy } from "./sections/alchemy.js";
import { copyText } from "./controls.js";

function ready(): boolean {
  return !!(
    window.HaikuFX &&
    window.HaikuFeed &&
    window.HaikuTheme &&
    window.HaikuThemeTokens &&
    window.HaikuAlchemy
  );
}

function boot(): void {
  const refs = buildSheet();
  buildWeather(refs.panels.weather);
  buildFeed(refs.panels.feed);
  buildChips(refs.chips);
  buildTheme(refs.panels.theme);
  buildAlchemy(refs.panels.alchemy);

  // footer: one aggregate copy of every tunable surface
  const copyAll = document.createElement("button");
  copyAll.type = "button";
  copyAll.dataset.testid = "lab-copy-all";
  copyAll.textContent = "Copy all";
  const allPre = document.createElement("pre");
  allPre.className = "lab-json";
  allPre.dataset.testid = "lab-json-all";
  copyAll.addEventListener("click", () => {
    allPre.textContent = JSON.stringify(copyAllJSON(), null, 2);
    // navigator.clipboard is undefined in non-secure contexts (plain http),
    // so calling it directly throws synchronously; copyText's try/catch
    // handles that (and denial in headless) as a soft failure — the JSON is
    // already visible in the pre either way.
    void copyText(allPre);
  });
  refs.footer.append(copyAll, allPre);

  window.HaikuLab = { state: getState, setState, tab: getTab, setTab };
}

function waitForGlobals(): void {
  if (ready()) {
    boot();
    return;
  }
  const startedAt = performance.now();
  const poll = (): void => {
    if (ready()) {
      boot();
    } else if (performance.now() - startedAt < 5000) {
      requestAnimationFrame(poll);
    }
  };
  requestAnimationFrame(poll);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", waitForGlobals);
} else {
  waitForGlobals();
}
